import { ReactNode, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { setUnauthorizedHandler } from "../api/client";
import { useStore } from "../store/store";

const RETURN_TO_KEY = "returnTo";

/** Remembers the address the user was after, for the login page to take them back to. */
export function rememberReturnTo({ pathname, search }: { pathname: string; search: string }): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, pathname + search);
  } catch {
    // Storage is blocked: after logging in the user starts from the server list.
  }
}

/** The remembered address, once; null when there is none or it points outside the app. */
export function takeReturnTo(): string | null {
  try {
    const saved = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    // "//host" and "https://host" would leave the app, and so would "/\host", which a browser reads as "//host".
    return saved && /^\/(?![/\\])/.test(saved) ? saved : null;
  } catch {
    return null;
  }
}

/** Shows `children` to a logged-in user; anyone else is sent to the login page, which brings them back here afterwards. */
export const RequireAuth = observer(({ children }: { children: ReactNode }) => {
  const store = useStore();
  const location = useLocation();
  const loggedIn = Boolean(store.token);

  useEffect(() => {
    if (!loggedIn) rememberReturnTo(location);
  }, [loggedIn, location]);

  if (!loggedIn) return <Navigate to="/login" replace />;
  return <>{children}</>;
});

/**
 * Registers what happens when the API rejects the session: on a panel page the
 * user goes to the login page and returns to that page afterwards. The client
 * has already logged out by then. Public pages stay where they are.
 */
export function SessionExpiry(): null {
  const navigate = useNavigate();
  const location = useLocation();
  const current = useRef(location);

  useEffect(() => {
    current.current = location;
  }, [location]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      const { pathname, search } = current.current;
      if (pathname !== "/dashboard" && !pathname.startsWith("/dashboard/")) return;

      rememberReturnTo({ pathname, search });
      navigate("/login", { replace: true });
    });
    return () => setUnauthorizedHandler(() => {});
  }, [navigate]);

  return null;
}
